import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import advertisementApi from '../../services/advertisementApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const AdvertisementEditModal = ({ advertisement, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('admin');
  const { authToken } = useAuth();
  const { setLoading, setError } = useAdminStore();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    linkUrl: '',
    position: 'sidebar',
    displayOrder: 0,
    isActive: true,
    startDate: '',
    endDate: '',
    maxImpressions: '',
    maxClicks: '',
    targetAudience: 'free',
  });

  useEffect(() => {
    if (advertisement) {
      setFormData({
        title: advertisement.title || '',
        description: advertisement.description || '',
        imageUrl: advertisement.imageUrl || '',
        linkUrl: advertisement.linkUrl || '',
        position: advertisement.position || 'sidebar',
        displayOrder: advertisement.displayOrder || 0,
        isActive: advertisement.isActive !== undefined ? advertisement.isActive : true,
        startDate: advertisement.startDate ? advertisement.startDate.split('T')[0] : '',
        endDate: advertisement.endDate ? advertisement.endDate.split('T')[0] : '',
        maxImpressions: advertisement.maxImpressions || '',
        maxClicks: advertisement.maxClicks || '',
        targetAudience: advertisement.targetAudience || 'free',
      });
    }
  }, [advertisement]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await advertisementApi.updateAdvertisement({
        token: authToken,
        adId: advertisement.id,
        adData: {
          title: formData.title,
          description: formData.description,
          imageUrl: formData.imageUrl,
          linkUrl: formData.linkUrl,
          position: formData.position,
          displayOrder: parseInt(formData.displayOrder) || 0,
          isActive: formData.isActive,
          startDate: formData.startDate || null,
          endDate: formData.endDate || null,
          maxImpressions: formData.maxImpressions ? parseInt(formData.maxImpressions) : null,
          maxClicks: formData.maxClicks ? parseInt(formData.maxClicks) : null,
          targetAudience: formData.targetAudience,
        },
      });

      if (result.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(result.error || t('advertisementEditModal.updateFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('advertisementEditModal.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-2xl border border-white/10 dark:border-white/10 my-8">
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">{t('advertisementEditModal.title')}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.titleLabel')}
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.positionLabel')}
                    </label>
                    <select
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      required
                    >
                      <option value="sidebar">{t('advertisementForm.positions.sidebar')}</option>
                      <option value="notes_list">{t('advertisementForm.positions.notes_list')}</option>
                      <option value="modal">{t('advertisementForm.positions.modal')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('advertisementForm.descriptionLabel')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.imageUrlLabel')}
                    </label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.linkUrlLabel')}
                    </label>
                    <input
                      type="url"
                      value={formData.linkUrl}
                      onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.displayOrderLabel')}
                    </label>
                    <input
                      type="number"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.targetAudienceLabel')}
                    </label>
                    <select
                      value={formData.targetAudience}
                      onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    >
                      <option value="free">{t('advertisementForm.audiences.free')}</option>
                      <option value="plus">{t('advertisementForm.audiences.plus')}</option>
                      <option value="all">{t('advertisementForm.audiences.all')}</option>
                    </select>
                  </div>

                  <div className="flex items-center pt-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="w-4 h-4 text-[#3d8ef0] rounded focus:ring-[#3d8ef0]/50"
                      />
                      <span className="text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300">{t('advertisementForm.activeLabel')}</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.startDateLabel')}
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.endDateLabel')}
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.maxImpressionsLabel')}
                    </label>
                    <input
                      type="number"
                      value={formData.maxImpressions}
                      onChange={(e) => setFormData({ ...formData, maxImpressions: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      min="1"
                      placeholder={t('advertisementForm.unlimitedPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                      {t('advertisementForm.maxClicksLabel')}
                    </label>
                    <input
                      type="number"
                      value={formData.maxClicks}
                      onChange={(e) => setFormData({ ...formData, maxClicks: e.target.value })}
                      className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                      min="1"
                      placeholder={t('advertisementForm.unlimitedPlaceholder')}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-white/10 dark:border-white/10">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] rounded-lg transition-colors"
                  >
                    {t('advertisementForm.cancel')}
                  </button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>{t('advertisementEditModal.save')}</span>
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AdvertisementEditModal;




